import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, RadialBarChart, RadialBar
} from 'recharts';
import {
  Activity, AlertTriangle, CheckCircle, Clock, Droplets, Package,
  RefreshCw, Shield, Ticket, Wrench, TrendingUp, TrendingDown,
  Users, Gauge, Factory, Zap, Timer, DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#0052CC', '#36B37E', '#FF5630', '#6554C0', '#FF8B00', '#00B8D9'];

const ExecutiveDashboardPage = () => {
  const { selectedPlant } = useData();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [advData, setAdvData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      const [res, advRes] = await Promise.all([
        api.get('/executive-dashboard/summary', { params }),
        api.get('/analytics/advanced-analytics', { params: { ...params, months: 6 } }).catch(() => ({ data: null }))
      ]);
      setData(res.data);
      setAdvData(advRes.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const { health_scores, equipment, tickets, lubrication, maintenance, stock, iscir, trend_7_days, top_problematic_equipment, top_technicians } = data;

  const healthColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-orange-500';
    return 'text-red-600';
  };

  const healthBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const radialData = [
    { name: 'Echipamente', value: health_scores.equipment, fill: '#0052CC' },
    { name: 'Lubrifiere', value: health_scores.lubrication, fill: '#36B37E' },
    { name: 'ISCIR', value: health_scores.iscir, fill: '#6554C0' },
    { name: 'Mentenanta', value: health_scores.maintenance, fill: '#FF8B00' },
  ];

  const pieEquipment = [
    { name: 'Sanatoase', value: equipment.healthy, fill: '#36B37E' },
    { name: 'Problematice', value: equipment.problematic, fill: '#FF5630' },
  ];

  return (
    <div className="space-y-6" data-testid="executive-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Dashboard Executiv</h1>
          <p className="text-slate-500 mt-1">Vizualizare unificata a performantei operationale</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualizeaza
        </Button>
      </div>

      {/* Overall Health Score */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${healthBg(health_scores.overall)}`}>
                <span className="text-2xl font-bold">{health_scores.overall}%</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold">Scor Sanatate Generala</h2>
                <p className="text-slate-300 text-sm">Medie din toate modulele</p>
              </div>
            </div>
            <div className="flex gap-8 flex-wrap">
              {[
                { label: 'Echipamente', value: health_scores.equipment, icon: Factory },
                { label: 'Lubrifiere', value: health_scores.lubrication, icon: Droplets },
                { label: 'ISCIR', value: health_scores.iscir, icon: Shield },
                { label: 'Mentenanta', value: health_scores.maintenance, icon: Wrench },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <item.icon className="w-5 h-5 mx-auto mb-1 opacity-70" />
                  <p className={`text-2xl font-bold ${health_scores[item.label.toLowerCase()] >= 80 ? 'text-green-400' : health_scores[item.label.toLowerCase()] >= 60 ? 'text-orange-400' : 'text-red-400'}`}>
                    {item.value}%
                  </p>
                  <p className="text-xs text-slate-400">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Analytics Summary */}
      {advData?.summary && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" /> Indicatori Avansati (6 luni)
            </h3>
            <Button variant="ghost" size="sm" className="text-xs text-blue-600" onClick={() => navigate('/analytics/advanced')} data-testid="goto-advanced-analytics">
              Detalii <TrendingUp className="w-3 h-3 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1"><Timer className="w-3.5 h-3.5 text-blue-500" /><span className="text-[10px] text-slate-500 font-semibold">MTTR</span></div>
              <p className="text-lg font-black text-blue-700">{advData.summary.avg_mttr}h</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1"><Shield className="w-3.5 h-3.5 text-emerald-500" /><span className="text-[10px] text-slate-500 font-semibold">MTBF</span></div>
              <p className="text-lg font-black text-emerald-700">{advData.summary.avg_mtbf}h</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3.5 h-3.5 text-amber-500" /><span className="text-[10px] text-slate-500 font-semibold">Cost Total</span></div>
              <p className="text-lg font-black text-amber-700">{(advData.summary.total_cost / 1000).toFixed(1)}k</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /><span className="text-[10px] text-slate-500 font-semibold">Defecte</span></div>
              <p className="text-lg font-black text-red-700">{advData.summary.total_failures}</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1"><Wrench className="w-3.5 h-3.5 text-purple-500" /><span className="text-[10px] text-slate-500 font-semibold">Prev/Cor</span></div>
              <p className="text-lg font-black text-purple-700">{advData.preventive_vs_corrective?.ratio || 0}:1</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1"><Users className="w-3.5 h-3.5 text-cyan-500" /><span className="text-[10px] text-slate-500 font-semibold">Tehnicieni</span></div>
              <p className="text-lg font-black text-cyan-700">{advData.summary.technicians_active}</p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Factory className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-slate-500">Echipamente</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{equipment.total}</p>
            <p className="text-xs text-slate-400">{equipment.problematic} cu probleme</p>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${tickets.critical > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Ticket className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-slate-500">Tichete Deschise</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{tickets.open + tickets.in_progress}</p>
            {tickets.critical > 0 && (
              <Badge className="bg-red-100 text-red-700 text-xs">{tickets.critical} critice</Badge>
            )}
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${lubrication.overdue > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Droplets className="w-4 h-4 text-cyan-500" />
              <span className="text-xs text-slate-500">Lubrifiere</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{lubrication.compliance}%</p>
            {lubrication.overdue > 0 && (
              <Badge className="bg-red-100 text-red-700 text-xs">{lubrication.overdue} intarziate</Badge>
            )}
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${maintenance.overdue > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-slate-500">Mentenanta</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{maintenance.pending}</p>
            {maintenance.overdue > 0 && (
              <Badge className="bg-red-100 text-red-700 text-xs">{maintenance.overdue} depasit</Badge>
            )}
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${stock.low_stock_articles > 0 ? 'border-l-orange-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-slate-500">Stoc Scazut</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stock.low_stock_articles}</p>
            <p className="text-xs text-slate-400">articole</p>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${iscir.expired > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-indigo-500" />
              <span className="text-xs text-slate-500">ISCIR</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{iscir.compliance}%</p>
            {iscir.expired > 0 && (
              <Badge className="bg-red-100 text-red-700 text-xs">{iscir.expired} expirate</Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Trend Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Trend Activitate (7 zile)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trend_7_days}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="tickets_created" name="Tichete Create" stroke="#FF5630" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="tickets_resolved" name="Tichete Rezolvate" stroke="#36B37E" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="lubrication_done" name="Lubrifieri" stroke="#0052CC" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Health Radial */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gauge className="w-5 h-5 text-purple-500" />
              Scoruri Sanatate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={radialData} startAngle={180} endAngle={0}>
                <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={5} />
                <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" />
                <Tooltip formatter={(val) => `${val}%`} />
              </RadialBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Problematic Equipment */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Echipamente Problematice
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_problematic_equipment.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Toate echipamentele sunt OK!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {top_problematic_equipment.map((eq, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50">
                    <span className="text-sm font-medium text-slate-700 truncate flex-1">{eq.equipment}</span>
                    <Badge className="bg-red-100 text-red-700">{eq.tickets} tichete</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Technicians */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Top Tehnicieni (7 zile)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_technicians.length === 0 ? (
              <p className="text-slate-400 text-center py-8">Nicio activitate</p>
            ) : (
              <div className="space-y-3">
                {top_technicians.map((tech, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-600' : 'bg-slate-300'}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{tech.name}</p>
                      <p className="text-xs text-slate-400">{tech.hours}h lucrate</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">{tech.resolved} rezolvate</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Sumar Rapid
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded bg-blue-50">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-slate-700">Tichete saptamana</span>
              </div>
              <span className="font-bold text-blue-700">+{tickets.created_week}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-green-50">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-slate-700">Rezolvate saptamana</span>
              </div>
              <span className="font-bold text-green-700">{tickets.resolved_week}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-purple-50">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-slate-700">Mentenante luna</span>
              </div>
              <span className="font-bold text-purple-700">{maintenance.completed_month}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-amber-50">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-slate-700">Consum piese (RON)</span>
              </div>
              <span className="font-bold text-amber-700">{stock.parts_value_month.toLocaleString('ro-RO')}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExecutiveDashboardPage;
